import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { IFormField } from "@/types/data-models";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export type FormFieldMap = Record<string, IFormField>;

interface FormFieldsEditorProps {
  fields: FormFieldMap;
  onChange: (next: FormFieldMap) => void;
  addLabel?: string;
  emptyMessage?: string;
}

export function FormFieldsEditor({
  fields,
  onChange,
  addLabel = "Add Field",
  emptyMessage = "No form fields added yet",
}: FormFieldsEditorProps) {
  const handleFieldUpdate = (
    key: string,
    property: keyof IFormField,
    value: string | boolean,
  ) => {
    onChange({
      ...fields,
      [key]: { ...fields[key], [property]: value },
    });
  };

  const removeField = (key: string) => {
    const next = { ...fields };
    delete next[key];
    onChange(next);
  };

  const addField = () => {
    const hasEmptyTitle = Object.values(fields).some(
      (field) => field.title.trim() === "",
    );
    if (hasEmptyTitle) {
      toast.error("Please fill in the empty field title before adding a new one");
      return;
    }
    const newKey = `field_${Object.keys(fields).length + 1}`;
    onChange({
      ...fields,
      [newKey]: { title: "", helper_text: "", display: true, required: false },
    });
  };

  return (
    <div className="space-y-3">
      {Object.keys(fields).length > 0 ? (
        Object.entries(fields).map(([key, field]) => (
          <div
            key={key}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors focus-within:border-foreground/30"
          >
            <div className="flex-1 space-y-2">
              <Input
                value={field.title}
                onChange={(e) => handleFieldUpdate(key, "title", e.target.value)}
                placeholder="Field title"
              />
              <Textarea
                value={field.helper_text}
                onChange={(e) =>
                  handleFieldUpdate(key, "helper_text", e.target.value)
                }
                placeholder="Helper text"
                rows={1}
              />
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Display
                </Label>
                <Switch
                  checked={field.display}
                  onCheckedChange={(checked) =>
                    handleFieldUpdate(key, "display", checked)
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Required
                </Label>
                <Switch
                  checked={field.required}
                  onCheckedChange={(checked) =>
                    handleFieldUpdate(key, "required", checked)
                  }
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => removeField(key)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))
      ) : (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      )}
      <Button variant="outline" onClick={addField}>
        <Plus className="mr-2 h-4 w-4" />
        {addLabel}
      </Button>
    </div>
  );
}

/**
 * Filter out fields with empty titles before persisting.
 * Both Applications and Portco Updates pages call this in their save handler.
 */
export function filterEmptyFields(fields: FormFieldMap): FormFieldMap {
  return Object.entries(fields).reduce<FormFieldMap>((acc, [key, field]) => {
    if (field.title.trim() !== "") acc[key] = field;
    return acc;
  }, {});
}
